import { useTranslation } from "react-i18next";

import type { TaxesBreakdown } from "../incomeCalculator/incomeCalculator.types";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface IncomeTaxesPanelProperties {
  panelId: string;
  /** Per-period / YTD taxes computed from Gross Monthly Pay; null = no basis yet. */
  taxes: TaxesBreakdown | null;
}

// ---------------------------------------------------------------------------
// USD formatter
// ---------------------------------------------------------------------------
const USD_FORMATTER = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function formatUsd(value: number): string {
  return USD_FORMATTER.format(value);
}

// ---------------------------------------------------------------------------
// IncomeTaxesPanel
// Single-row taxes table: per-period Social Security / Medicare / Combined.
// The table always renders; values are em-dash placeholders until a Gross
// Monthly Pay basis exists (taxes !== null).
// ---------------------------------------------------------------------------
export function IncomeTaxesPanel({ panelId, taxes }: IncomeTaxesPanelProperties) {
  const { t } = useTranslation();
  const EMPTY = "—";

  return (
    <div id={panelId} className="bg-surface-subtle border-primary/30 w-fit rounded-md border px-3 py-3 shadow-sm">
      <div className="bg-card w-fit overflow-x-auto rounded-md">
        <table className="w-auto text-xs">
          <caption className="sr-only">{t("review.income.taxes.title")}</caption>
          <thead>
            <tr className="bg-surface-subtle border-border border-b">
              <th scope="col" className="text-muted-foreground border-border border-r px-5 py-1.5 text-center font-medium">
                {t("review.income.calculator.results.columns.socialSecurity")}
              </th>
              <th scope="col" className="text-muted-foreground border-border border-r px-5 py-1.5 text-center font-medium">
                {t("review.income.calculator.results.columns.medicare")}
              </th>
              <th scope="col" className="text-muted-foreground px-5 py-1.5 text-center font-medium">
                {t("review.income.calculator.results.columns.combined")}
              </th>
            </tr>
          </thead>
          <tbody className="text-foreground">
            <tr>
              <td className="border-border border-r px-5 py-1.5 text-center">
                {taxes === null ? EMPTY : formatUsd(taxes.socialSecurityPeriod)}
              </td>
              <td className="border-border border-r px-5 py-1.5 text-center">
                {taxes === null ? EMPTY : formatUsd(taxes.medicarePeriod)}
              </td>
              <td className="px-5 py-1.5 text-center">
                {taxes === null ? EMPTY : formatUsd(taxes.ficaCombinedPeriod)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
